<template>
  <page-container :loading="loading" class="home">
    <div>
      <UserWelcomeDialog :lessons="lessons" ref="UserWelcomeDialog" />
      <LessonLinkDialog ref="LessonLinkDialog" />
      <AppHeader :name="user.name" />
      <v-container>
        <div class="container--small home__content">
          <v-row align="center" class="mb-3 home__content--header" no-gutters>
            <v-col>
              <h2 class="future-events">{{ $t('futureEvents') }}</h2>
            </v-col>
            <v-col class="d-flex justify-end">
              <v-btn
                color="primary"
                large
                depressed
                rounded
                @click="() => $refs.LessonCreateDialog.open()"
                class="button-create px-12"
              >{{ $t('lessonCreate') }}</v-btn>

              <LessonCreateDialog
                @show-link="showLink"
                :lessons="lessons"
                ref="LessonCreateDialog"
              />
            </v-col>
          </v-row>

          <v-row v-if="lessonsUpcoming.length">
            <v-col v-for="(lesson, index) in lessonsUpcoming" :key="index" md="4" sm="6" cols="12">
              <LessonBoxUpcoming
                :lesson="lesson"
                :id="index"
                @open-delete="openDelete"
                @recycle-lesson="recycleLesson"
              />
            </v-col>
          </v-row>
          <!-- eslint-disable-next-line max-len -->
          <lesson-window-alert
            class="home__alert"
            :message="$t('lessonEmpty')"
            :icon="'mdi-television-play'"
            v-else
          ></lesson-window-alert>
          <div class="mt-12" v-if="lessonsEnded.length">
            <h2 class="mb-6">{{ $t('lessonHistory') }}</h2>
            <div class="pb-12">
              <LessonBoxEnded
                v-for="(lesson, index) in lessonsEnded"
                :key="index"
                :lesson="lesson"
                @open-delete="openDelete"
                @recycle-lesson="recycleLesson"
              />
            </div>
          </div>
          <v-dialog v-model="dialog" max-width="360">
            <v-card>
              <v-card-title class="headline">{{ $t('deteleTitle') }}</v-card-title>
              <v-card-text>{{ $t('deteleSubtitle') }}</v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="#8b8b9d" text @click="dialog = false">{{ $t('dialogCancel') }}</v-btn>
                <v-btn
                  color="#ff808b"
                  text
                  @click="deleteLesson"
                >{{ $t('dialogDelete').toUpperCase() }}</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </div>
      </v-container>
    </div>
  </page-container>
</template>

<script>
import { db } from '@/db';

import AppHeader from '@/components/AppHeader.vue';
import LessonCreateDialog from '@/components/LessonCreateDialog.vue';
import UserWelcomeDialog from '@/components/UserWelcomeDialog.vue';
import PageContainer from '@/components/PageContainer.vue';
import LessonBoxUpcoming from '@/components/LessonBoxUpcoming.vue';
import LessonBoxEnded from '@/components/LessonBoxEnded.vue';
import LessonWindowAlert from '@/components/LessonWindowAlert.vue';
import LessonLinkDialog from '@/components/LessonLinkDialog.vue';
import { mapGetters } from 'vuex';

export default {
  name: 'Home',
  components: {
    AppHeader,
    UserWelcomeDialog,
    LessonCreateDialog,
    LessonBoxUpcoming,
    LessonBoxEnded,
    LessonWindowAlert,
    PageContainer,
    LessonLinkDialog,
  },
  metaInfo() {
    return {
      title: this.$t('projectTitle'),
      titleTemplate: `${this.$t('lessonList')}`,
    };
  },
  data() {
    return {
      lessons: [],
      showIntro: false,
      user: {},
      isLessonsLoading: true,
      isUserLoading: true,
      dialog: false,
      dialogLessonToDelete: '',
    };
  },

  computed: {
    ...mapGetters(['auth', 'welcomeDialog']),

    lessonsUpcoming() {
      return this.lessons
        .filter(({ status }) => status !== 'ended')
        .sort((a, b) => a.start.valueOf() - b.start.valueOf());
    },

    loading() {
      return this.isLessonsLoading || this.isUserLoading;
    },

    lessonsEnded() {
      return this.lessons
        .filter(({ status }) => status === 'ended')
        .sort((a, b) => a.start.valueOf() - b.start.valueOf())
        .reverse();
    },
  },

  created() {
    this.pullAll();
    this.$store.dispatch('logEvent', {
      name: 'home_loaded',
    });
  },
  methods: {
    async pullAll() {
      await this.$bind('user', db.collection('users').doc(this.auth.email));
      this.isUserLoading = false;
      await this.$bind(
        'lessons',
        db.collection('lessons').where('author', '==', this.auth.email),
      );
      this.isLessonsLoading = false;
      if (!this.user.introSeen) {
        this.$store.dispatch('updateDb', {
          type: 'users',
          id: this.auth.email,
          item: 'introSeen',
          value: true,
        }).then(() => {
          if (!this.welcomeDialog) {
            this.$refs.UserWelcomeDialog.openDialog();
          }
        });
      }
    },
    showLink(lesson) {
      this.$refs.LessonCreateDialog.close();
      setTimeout(() => {
        this.$refs.LessonLinkDialog.openDialog(lesson);
      }, 250);
    },
    openDelete(id) {
      this.dialog = true;
      this.dialogLessonToDelete = id;
    },
    recycleLesson(id) {
      const lesson = [...this.lessonsUpcoming, ...this.lessonsEnded].find(
        l => l.id === id,
      );
      this.$refs.LessonCreateDialog.openAndPrefill({
        title: lesson.title,
        emails: Object.keys(lesson.viewers).map(k => lesson.viewers[k].email),
      });
    },
    deleteLesson() {
      this.$store.dispatch('deleteFromDb', {
        type: 'lessons',
        id: this.dialogLessonToDelete,
      });
      this.dialog = false;
      this.dialogLessonToDelete = '';
    },
  },
};
</script>

<style lang="scss" scoped>
h2 {
  margin-left: 25px;
}

.container--small {
  max-width: 780px;
  @media (min-width: 1440px) {
    max-width: 1080px;
  }
}

.home {
  background-color: #f5f5fb !important;
  min-height: 100vh;

  &__content {
    margin: 0 auto;
  }

  &__alert {
    height: 200px;
    border: 2px solid #e4e4ec;
    border-radius: 5px;
    margin-top: 50px;
  }
}

@media only screen and (max-width: 968px) {
  .home__content--header {
    flex-direction: column-reverse;
    text-align: center;
  }
  .button-create {
    width: 100% !important;
  }
  .future-events {
    padding-top: 20px !important;
    margin-left: 0px !important;
  }
}
</style>
